import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { alpha, Box, Button, IconButton, Typography } from '@mui/material'
import { Close, KeyboardDoubleArrowRight } from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { selectOiStrikeFilterLtp } from '../../../../selectors/security'
import { selectUnderlyingOptionsExpiriesV2 } from '../../../../selectors/options'
import { findNearestNumber } from '../../../../util/tradepanel'
import { selectIsEmbedded } from '../../../../selectors/device'
import { green, red } from '../../../../assets/colors'

type Props = {
    underlyingToken: string
    expiry: string
    nearAtm?: number
    atmSelected?: number
    autoRefresh: boolean
    notifyInterval: number
    priceMultiplier: number
    onRefreshNearAtm: (nearAtm: number, driftedAtmStrike?: number) => void
    notificationContainer?: HTMLElement | null
}

type DriftMode = 'reset' | 'info'

/** Live LTP / ATM notification — isolated so strike menu button does not rerender on ticks. */
const MultiStrikesAtmSideEffects = ({
    underlyingToken,
    expiry,
    nearAtm,
    atmSelected,
    autoRefresh,
    notifyInterval,
    priceMultiplier,
    onRefreshNearAtm,
    notificationContainer,
}: Props) => {
    const strikeFilterLtp = useSelector(selectOiStrikeFilterLtp(underlyingToken))
    const optionsExpiries = useSelector(selectUnderlyingOptionsExpiriesV2(underlyingToken))
    const isEmbedded = useSelector(selectIsEmbedded)
    const [open, setOpen] = useState(false)
    const [mode, setMode] = useState<DriftMode>('reset')
    const [drift, setDrift] = useState<{ from?: number; to?: number }>({})

    const calculateAtmStrike = useMemo(() => {
        if (!optionsExpiries?.length) return null
        const expiryIndex = optionsExpiries.findIndex((o) => o.expiry.name === expiry)
        if (expiryIndex < 0) return null
        const expiryData = optionsExpiries[expiryIndex]
        if (!expiryData || !strikeFilterLtp) return null
        return findNearestNumber(expiryData.strikesArray, strikeFilterLtp)
    }, [optionsExpiries, expiry, strikeFilterLtp])

    // Keep the latest values for the stable interval below so frequent LTP ticks
    // don't continuously reset the timer.
    const latest = useRef({ calculateAtmStrike, atmSelected, autoRefresh, nearAtm, onRefreshNearAtm })
    latest.current = { calculateAtmStrike, atmSelected, autoRefresh, nearAtm, onRefreshNearAtm }

    // When Near-ATM strikes are selected, periodically check whether the ATM strike drifted.
    //  - auto-refresh OFF -> notify with a RESET action so the user can re-center manually.
    //  - auto-refresh ON  -> re-center the strikes automatically and notify for the record.
    useEffect(() => {
        const intervalMs = (notifyInterval || 30) * 1000
        const check = () => {
            const { calculateAtmStrike: next, atmSelected: atm, autoRefresh: auto, nearAtm: na, onRefreshNearAtm: refresh } = latest.current
            if (!na || next === null || next === atm) return
            if (isNaN(atm ?? NaN) || isNaN(next)) return
            setDrift({ from: atm, to: next })
            if (auto) {
                refresh(na, next)
                setMode('info')
            } else {
                setMode('reset')
            }
            setOpen(true)
        }
        const intervalId = setInterval(check, intervalMs)
        return () => clearInterval(intervalId)
    }, [notifyInterval])

    useEffect(() => {
        if (!open) return
        const timeoutId = setTimeout(() => setOpen(false), 8000)
        return () => clearTimeout(timeoutId)
    }, [open])

    const handleReset = useCallback(() => {
        if (nearAtm) onRefreshNearAtm(nearAtm, drift.to)
        setOpen(false)
    }, [nearAtm, onRefreshNearAtm, drift.to])

    const handleDismiss = useCallback(() => setOpen(false), [])

    if (isEmbedded || !open) return null

    const fromStrike = (drift.from ?? 0) / priceMultiplier
    const toStrike = (drift.to ?? 0) / priceMultiplier
    const arrowColor = toStrike > fromStrike ? green : toStrike < fromStrike ? red : 'text.secondary'

    const buildNotification = (notifMode: DriftMode) => (
        <Box
            key={notifMode}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                height: 32,
                pl: 1.5,
                pr: 0.5,
                borderRadius: 1,
                whiteSpace: 'nowrap',
                backgroundColor: 'background.paper',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: 2
            }}
        >
            <Typography
                variant="body2"
                color="text.primary"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
                ATM drifted {fromStrike}
                <KeyboardDoubleArrowRight sx={{ fontSize: 18, color: arrowColor }} />
                {toStrike}.{notifMode === 'info' ? ' Strikes updated.' : ''}
            </Typography>
            {notifMode === 'reset' && (
                <Button
                    size="small"
                    onClick={handleReset}
                    sx={{
                        minWidth: 'auto',
                        px: 1,
                        color: 'primary.main',
                        fontWeight: 600,
                        letterSpacing: '0.04em'
                    }}
                >
                    UPDATE
                </Button>
            )}
            <IconButton
                size="small"
                onClick={handleDismiss}
                sx={{
                    p: 0.25,
                    color: 'text.secondary',
                    '&:hover': {
                        color: 'text.primary',
                        backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08)
                    }
                }}
            >
                <Close sx={{ fontSize: 16 }} />
            </IconButton>
        </Box>
    )

    const notification = (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            {buildNotification(mode)}
        </Box>
    )

    if (!notificationContainer) return null

    return createPortal(notification, notificationContainer)
}

export default memo(MultiStrikesAtmSideEffects)
